import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Tab,
  Tabs,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Inventory2Outlined as OrdersIcon,
  Refresh as RefreshIcon,
  VisibilityOutlined as ViewIcon,
} from '@mui/icons-material';
import CollectionsTab from './CollectionsTab';
import DeliveryEvidenceDialog from '../../components/monitoring/DeliveryEvidenceDialog';
import PageHeader from '../../components/PageHeader';
import { APP_SHAPE } from '../../constants/shape';
import { getRecyclerOrdersMonitor } from '../../services/monitoring.service';
import { useOrganizationStore } from '../../stores/organization.store';
import { OrganizationType } from '../../types/organization.type';
import type { RecyclerOrderOverview, RecyclerPreOrderMonitor } from '../../types/monitoring.type';

const emptyOverview: RecyclerOrderOverview = {
  total_orders: 0,
  total_ordered_kg: 0,
  fulfilled_kg: 0,
  remaining_kg: 0,
  pending_count: 0,
  partially_fulfilled_count: 0,
  completed_count: 0,
  cancelled_count: 0,
};

const humanize = (value: string | null) => (value ?? '—').replace(/_/g, ' ');

function MetricCard({ title, value }: { title: string; value: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.25, minWidth: 0, borderRadius: `${APP_SHAPE.radiusLg}px` }}>
      <Typography variant="body2" color="text.secondary">{title}</Typography>
      <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 700 }}>{value}</Typography>
    </Paper>
  );
}

export default function OrdersMonitoring() {
  const { t, i18n } = useTranslation();
  const { selectedOrganization } = useOrganizationStore();
  const [orders, setOrders] = useState<RecyclerPreOrderMonitor[]>([]);
  const [overview, setOverview] = useState(emptyOverview);
  const [tab, setTab] = useState<'preorders' | 'collections'>('preorders');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [evidencePickupId, setEvidencePickupId] = useState<string | null>(null);
  const number = useMemo(
    () => new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language, { maximumFractionDigits: 1 }),
    [i18n.language, i18n.resolvedLanguage],
  );
  const formatDate = (value: string) => new Date(value).toLocaleString(i18n.resolvedLanguage || i18n.language);
  const translatedValue = (value: string | null) => {
    if (!value) return '—';
    return t(`monitoring.values.${value.toLowerCase()}`, { defaultValue: humanize(value) });
  };

  useEffect(() => {
    if (!selectedOrganization || selectedOrganization.organization_type !== OrganizationType.RECYCLER) return;
    let active = true;
    setLoading(true);
    setError(null);
    getRecyclerOrdersMonitor(selectedOrganization.id, page * rowsPerPage, rowsPerPage)
      .then((result) => {
        if (!active) return;
        setOrders(result.pre_orders);
        setOverview(result.overview);
        setTotal(result.total_count);
      })
      .catch((requestError) => {
        if (active) setError(requestError?.response?.data?.detail ?? t('monitoring.orders.loadError'));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [selectedOrganization, page, rowsPerPage, refreshKey, t]);

  if (!selectedOrganization) return <Alert severity="info">{t('monitoring.orders.selectOrganization')}</Alert>;
  // Pre-orders monitoring is recycler-only; route-run collections apply to both
  // recyclers and waste movers, so the page tabs rather than gating whole-hog.
  const isRecycler = selectedOrganization.organization_type === OrganizationType.RECYCLER;
  const activeTab = isRecycler ? tab : 'collections';

  return (
    <Box>
      <PageHeader
        icon={<OrdersIcon />}
        title={t('monitoring.orders.title')}
        subtitle={t('monitoring.orders.subtitle', { name: selectedOrganization.name })}
        sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }}
        actions={<Button startIcon={<RefreshIcon />} onClick={() => setRefreshKey((value) => value + 1)} disabled={loading}>{t('common.refresh')}</Button>}
      />

      {isRecycler ? (
        <Tabs value={activeTab} onChange={(_, next) => setTab(next)} sx={{ mb: 2 }}>
          <Tab value="preorders" label={t('monitoring.orders.tabPreOrders')} />
          <Tab value="collections" label={t('collectionsMonitor.tabLabel')} />
        </Tabs>
      ) : null}

      {activeTab === 'collections' ? <CollectionsTab /> : (
      <>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
        <MetricCard title={t('monitoring.orders.metrics.totalOrders')} value={number.format(overview.total_orders)} />
        <MetricCard title={t('monitoring.orders.metrics.orderedQuantity')} value={t('monitoring.quantityKg', { value: number.format(overview.total_ordered_kg) })} />
        <MetricCard title={t('monitoring.orders.metrics.fulfilledQuantity')} value={t('monitoring.quantityKg', { value: number.format(overview.fulfilled_kg) })} />
        <MetricCard title={t('monitoring.orders.metrics.remainingQuantity')} value={t('monitoring.quantityKg', { value: number.format(overview.remaining_kg) })} />
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }}>
        {loading && <LinearProgress />}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={48} />
              <TableCell>{t('monitoring.orders.columns.order')}</TableCell>
              <TableCell>{t('monitoring.columns.material')}</TableCell>
              <TableCell>{t('common.status')}</TableCell>
              <TableCell align="right">{t('monitoring.orders.columns.ordered')}</TableCell>
              <TableCell align="right">{t('monitoring.orders.columns.fulfilled')}</TableCell>
              <TableCell width={190}>{t('monitoring.orders.columns.progress')}</TableCell>
              <TableCell>{t('monitoring.columns.created')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && orders.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6 }}>{t('monitoring.orders.empty')}</TableCell></TableRow>
            )}
            {orders.map((order) => (
              <Fragment key={order.id}>
                <TableRow hover>
                  <TableCell>
                    <IconButton size="small" aria-label={t('monitoring.orders.showLinkedPickups')} onClick={() => setExpanded(expanded === order.id ? null : order.id)}>
                      {expanded === order.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell><Typography variant="body2" fontWeight={600}>#{order.id.slice(0, 8)}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{order.material_name_snapshot ?? translatedValue(order.material_category)}</Typography>
                    {order.category_name_snapshot && <Typography variant="caption" color="text.secondary">{order.category_name_snapshot}</Typography>}
                    {(order.regulatory_code_snapshot || order.physical_state_snapshot) && (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                        {order.regulatory_code_snapshot && <Chip size="small" variant="outlined" label={`EWC ${order.regulatory_code_snapshot}`} />}
                        {order.physical_state_snapshot && <Chip size="small" variant="outlined" label={translatedValue(order.physical_state_snapshot)} />}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell><Chip size="small" label={translatedValue(order.status)} color={order.status === 'completed' ? 'success' : order.status === 'cancelled' ? 'default' : 'warning'} /></TableCell>
                  <TableCell align="right">{t('monitoring.quantityKg', { value: number.format(order.amount_kg) })}</TableCell>
                  <TableCell align="right">{t('monitoring.quantityKg', { value: number.format(order.fulfilled_kg) })}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress variant="determinate" value={Math.min(100, order.fulfillment_percent)} sx={{ flex: 1, height: 7, borderRadius: 4 }} />
                      <Typography variant="caption">{number.format(order.fulfillment_percent)}%</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{formatDate(order.created_at)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={8} sx={{ p: 0, borderBottom: expanded === order.id ? undefined : 0 }}>
                    <Collapse in={expanded === order.id} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 2.5, bgcolor: 'action.hover' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('monitoring.orders.linkedActivity')}</Typography>
                        {order.linked_pickups.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">{t('monitoring.orders.noLinkedPickups')}</Typography>
                        ) : (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>{t('monitoring.orders.columns.pickup')}</TableCell>
                                <TableCell>{t('monitoring.columns.wasteOutlet')}</TableCell>
                                <TableCell>{t('monitoring.orders.columns.pickupStatus')}</TableCell>
                                <TableCell>{t('monitoring.orders.columns.deliveryPhase')}</TableCell>
                                <TableCell align="right">{t('monitoring.orders.columns.verifiedWeight')}</TableCell>
                                <TableCell align="right">{t('monitoring.orders.columns.evidence')}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>{order.linked_pickups.map((pickup) => (
                              <TableRow key={pickup.pickup_order_id}>
                                <TableCell>#{pickup.pickup_order_id.slice(0, 8)}</TableCell>
                                <TableCell>{pickup.waste_outlet_name ?? '—'}</TableCell>
                                <TableCell sx={{ textTransform: 'capitalize' }}>{translatedValue(pickup.status)}</TableCell>
                                <TableCell sx={{ textTransform: 'capitalize' }}>{translatedValue(pickup.delivery_phase)}</TableCell>
                                <TableCell align="right">{pickup.verified_weight_kg == null ? '—' : t('monitoring.quantityKg', { value: number.format(pickup.verified_weight_kg) })}</TableCell>
                                <TableCell align="right">
                                  <Tooltip title={t('monitoring.orders.viewEvidence')}>
                                    <IconButton size="small" aria-label={t('monitoring.orders.viewEvidence')} onClick={() => setEvidencePickupId(pickup.pickup_order_id)}>
                                      <ViewIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}</TableBody>
                          </Table>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </Fragment>
            ))}
          </TableBody>
        </Table>
        {loading && orders.length === 0 && <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>}
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, value) => setPage(value)}
          onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage={t('monitoring.pagination.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('monitoring.pagination.displayedRows', { from, to, count })}
          getItemAriaLabel={(type) => t(`monitoring.pagination.${type}Page`)}
        />
      </TableContainer>
      </>
      )}

      <DeliveryEvidenceDialog
        open={evidencePickupId != null}
        pickupOrderId={evidencePickupId}
        onClose={() => setEvidencePickupId(null)}
      />
    </Box>
  );
}
